import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts"

import type { DashboardChartBucket } from "../../services/use-dashboard"
import { formatBucketKey } from "../../lib/format-date"
import { convertToEur, type ExchangeRates } from "../../lib/money"
import { usePrefersReducedMotion } from "../../hooks/use-prefers-reduced-motion"
import { CHART_TOOLTIP_CONTENT_STYLE } from "../ui/chart-style"

type SalesChartProps = {
  buckets: DashboardChartBucket[]
  granularity: "day" | "month"
  currency: string | null
  rates: ExchangeRates | undefined
}

export function SalesChart({ buckets, granularity, currency, rates }: SalesChartProps) {
  const prefersReducedMotion = usePrefersReducedMotion()
  const nativeCurrency = currency ?? "EUR"
  // EUR is the primary axis. Plot converted GMV when a rate is available;
  // otherwise fall back to the native amount so the chart still renders.
  const canConvert = nativeCurrency === "EUR" || rates?.rates[nativeCurrency] != null
  const displayCurrency = canConvert ? "EUR" : nativeCurrency
  const showNative = displayCurrency === "EUR" && nativeCurrency !== "EUR"

  const data = buckets.map((b) => ({
    label: formatBucketKey(b.key, granularity),
    gmv: canConvert ? (convertToEur(b.gmv, nativeCurrency, rates) ?? b.gmv) : b.gmv,
    gmvNative: b.gmv,
    orderCount: b.orderCount,
  }))
  const moneyFmt = makeMoneyFormatter(displayCurrency)
  const nativeFmt = makeMoneyFormatter(nativeCurrency)

  return (
    <div className="h-[320px] w-full">
      <ResponsiveContainer width="100%" height="100%">
        <ComposedChart data={data} margin={{ top: 8, right: 24, left: 0, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" className="stroke-border" />

          <XAxis
            dataKey="label"
            tick={{ fontSize: 12 }}
            axisLine={false}
            tickLine={false}
            interval="preserveStartEnd"
          />

          <YAxis
            yAxisId="orders"
            orientation="left"
            tick={{ fontSize: 12 }}
            axisLine={false}
            tickLine={false}
            allowDecimals={false}
            width={32}
          />

          <YAxis
            yAxisId="gmv"
            orientation="right"
            tick={{ fontSize: 12 }}
            axisLine={false}
            tickLine={false}
            tickFormatter={(v: number) => moneyFmt.format(v)}
            width={56}
          />

          <Tooltip
            formatter={(value, name, item) => {
              if (name !== "gmv") {
                return [value, "Orders"]
              }
              const eur = moneyFmt.format(Number(value))
              const nativeValue = Number(item?.payload?.gmvNative ?? 0)
              return [showNative ? `${eur} · ${nativeFmt.format(nativeValue)}` : eur, "GMV"]
            }}
            contentStyle={CHART_TOOLTIP_CONTENT_STYLE}
            cursor={{ className: "fill-muted" }}
          />

          <Bar
            yAxisId="orders"
            dataKey="orderCount"
            fill="var(--color-muted-foreground)"
            opacity={0.25}
            radius={[3, 3, 0, 0]}
            isAnimationActive={!prefersReducedMotion}
            animationDuration={460}
            animationEasing="ease-out"
          />

          <Line
            yAxisId="gmv"
            type="monotone"
            dataKey="gmv"
            stroke="var(--color-foreground)"
            strokeWidth={2}
            dot={false}
            isAnimationActive={!prefersReducedMotion}
            animationDuration={560}
            animationEasing="ease-out"
          />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  )
}

function makeMoneyFormatter(currency: string | null): Intl.NumberFormat {
  // Locale stays neutral; the dashboard runs on the operator's browser locale.
  return new Intl.NumberFormat(undefined, {
    style: currency ? "currency" : "decimal",
    currency: currency ?? "EUR",
    maximumFractionDigits: 0,
  })
}
